// external
import { Component, Vue } from 'vue-property-decorator';
import { Getter, Action } from 'vuex-class';

// models
import {
	CallToActionType,
	ICallToAction,
	CallToActionPosition,
} from '@/interfaces/CallToActionTypes';
import { ICard } from '@/interfaces/Card';
import { IRetailer } from '@/interfaces/Retailer';
import { Partner } from '@/interfaces/Partner';
import {
	GET_CURRENT_CARD,
	GET_CURRENT_MXR_RETAILER,
	GET_CUSTOM_FOOTER_ENABLED,
	GET_CUSTOM_FOOTER_TEXT,
	GET_FOOTER_CTA,
	GET_INTEGRATED_RETAILER,
	GET_IS_DEFAULT,
	GET_MXR_CONFIG,
	GET_MXR_RETAILER_LOCATIONS,
	GET_RETAILER,
	GET_STORE_PICKER_ENABLED,
	GET_TAKEOVER,
	TOGGLE_RETAILER_LOCATIONS_MODAL,
} from '@/store/types';
import MxrRetailer from '@/interfaces/MxrRetailer';
import { VuexAction } from '@/@types';

// takeover
import { Takeover } from '@/takeover';
import { IMxrConfig } from '@/interfaces/MxrConfig';

@Component({})
export default class Footer extends Vue {
	private defaultFooterLogo =
		'https://cdn.selectablemedia.com/polaris/images/MXR_Retailer_100x100.png';

	@Getter(GET_CURRENT_CARD) getCurrentCard!: ICard;

	@Getter(GET_RETAILER) getRetailer!: IRetailer;

	@Getter(GET_MXR_CONFIG) getMxrConfig!: IMxrConfig;

	@Getter(GET_FOOTER_CTA) getFooterCta!: ICallToAction;

	@Getter(GET_CUSTOM_FOOTER_ENABLED) getCustomFooterEnabled!: boolean;

	@Getter(GET_CUSTOM_FOOTER_TEXT) getCustomFooterText!: string;

	@Getter(GET_CURRENT_MXR_RETAILER) getCurrentMxrRetailer!: MxrRetailer;

	@Getter(GET_INTEGRATED_RETAILER) getIntegratedRetailer: string | undefined;

	@Getter(GET_TAKEOVER) getTakeover!: Takeover;

	@Getter(GET_IS_DEFAULT) getIsDefault!: boolean;

	@Getter(GET_STORE_PICKER_ENABLED) getStorePickerEnabled!: boolean;

	@Getter(GET_MXR_RETAILER_LOCATIONS) getMxrRetailerLocations!: MxrRetailer[];

	@Action(TOGGLE_RETAILER_LOCATIONS_MODAL)
	toggleRetailerLocationsModal!: VuexAction<void>;

	/* Amazon Fresh
    ctaType: CallToActionType = CallToActionType.Partner;
    label: string = 'Buy Now';
    url: string = '';
    subject: string = '';
    body: string = '';
    partner: CallToActionPartner = CallToActionPartner.AmazonFresh;
    */

	/* Email
    ctaType: CallToActionType = CallToActionType.Email;
    label: string = 'Share';
    url: string = '';
    subject: string = 'Your Recipe';
    body: string = 'Get some good food';
    partner: CallToActionPartner = CallToActionPartner.Instacart;
    */

	/* Static
    ctaType: CallToActionType = CallToActionType.Static;
    label: string = 'Get Deal';
    url: string = 'https://www.youtube.com/watch?v=dQw4w9WgXcQ';
    subject: string = '';
    body: string = '';
    partner: CallToActionPartner = CallToActionPartner.Instacart;
    */

	/* Instacart */
	get retailerLogoAltAttribute(): string {
		return this.getRetailer && this.getRetailer.name
			? `${this.getRetailer.name} logo`
			: 'retailer logo';
	}

	get customFooterEnabledDefault(): boolean {
		if (
			this.getIsDefault ||
			(this.getTakeover && this.getMxrRetailerLocations.length === 0)
		) {
			return true;
		}
		return this.getCustomFooterEnabled;
	}

	get position(): CallToActionPosition {
		return CallToActionPosition.FOOTER;
	}

	get ctaType(): CallToActionType {
		return this.getFooterCta
			? this.getFooterCta.ctaType
			: CallToActionType.NONE;
	}

	get url(): string {
		if (!this.getFooterCta) {
			return '';
		}
		return this.ctaType === CallToActionType.PARTNER
			? this.getCurrentCard.callToActionUrl || ''
			: this.getFooterCta.url;
	}

	get subject(): string {
		return this.getFooterCta ? this.getFooterCta.emailSubject : '';
	}

	get body(): string {
		return this.getFooterCta ? this.getFooterCta.emailBody : '';
	}

	get partner(): Partner {
		return this.getFooterCta ? this.getFooterCta.partner : Partner.NONE;
	}

	get label() {
		return this.getFooterCta ? this.getFooterCta.label : '';
	}

	get showCheckoutTotal() {
		return this.getFooterCta ? this.getFooterCta.showCheckoutTotal : '';
	}

	private openRetailerLocations(): void {
		this.toggleRetailerLocationsModal();
	}

	get logoForIntegratedRetailer(): string {
		switch (this.getIntegratedRetailer) {
			case Partner.WALMART:
				return 'https://crud-cdn.polaris.me/assets/images/3b5d70e2-a9a6-4aa0-95de-4dd991e706f8/300';
			case Partner.KROGER:
				return 'https://cdn.polaris.me/img/kroger-square-logo.png';
			case Partner.TARGET:
				return 'https://crud-cdn.polaris.me/assets/images/06764f5c-3658-4e3f-bacd-e0c0e5dc15b9/300';
			case Partner.LOWES:
				return 'https://crud-cdn.polaris.me/assets/images/d040fbcc-35fd-4f94-97c7-b85a65a7a4a7/300';
			default:
				return '';
		}
	}

	get currentMXRRetailer(): MxrRetailer {
		return this.getCurrentMxrRetailer;
	}

	get logo(): string {
		if (
			this.getMxrConfig.footerLogo &&
			this.getMxrConfig.footerLogo !== this.defaultFooterLogo
		) {
			// A custom, non-default footerLogo has first priority
			return this.getMxrConfig.footerLogo;
		}

		if (
			!this.getMxrConfig.retailerIntegration &&
			!this.getIsDefault &&
			this.retailer.logo &&
			this.retailer.logo.length > 0
		) {
			// Non-RI retailer logos from grocery server
			return this.retailer.logo;
		}

		if (!this.getIsDefault && this.currentMXRRetailer.Logo.length > 0) {
			// Kroger RI chain logos
			return this.currentMXRRetailer.Logo;
		}

		if (this.getMxrConfig.retailerIntegration) {
			// If RI use the fallback logo for the RI retailer
			return this.logoForIntegratedRetailer;
		}

		// Finally use the default MXR_Retailer_100x100.png if there
		// are no other options
		return this.getMxrConfig.footerLogo ?? this.defaultFooterLogo;
	}

	get formattedFooterHeader() {
		if (this.getCurrentMxrRetailer.Preformatted) {
			return this.getCurrentMxrRetailer.Chain;
		}
		const header = this.getCurrentMxrRetailer.Chain
			? this.getCurrentMxrRetailer.Chain
			: this.getCurrentMxrRetailer.Partner;
		const headerWords = header.split(' ');
		for (let i = 0; i < headerWords.length; i++) {
			headerWords[i] =
				headerWords[i][0].toUpperCase() + headerWords[i].substr(1);
		}
		return headerWords.join(' ');
	}

	get retailerAddress(): string {
		if (!this.retailer.location || !this.retailer.location.address) {
			return '';
		}
		return this.retailer.location.address;
	}

	get retailerCityState(): string {
		if (
			!this.retailer.location ||
			!this.retailer.location.city ||
			!this.retailer.location.state
		) {
			return '';
		}
		return `${this.retailer.location.city}, ${this.retailer.location.state}`;
	}

	get retailer(): IRetailer {
		return this.getRetailer;
	}
}
